/* eslint-disable indent */
import { useState, useRef, useEffect, useCallback } from "react";
// @mui
import { styled } from "@mui/material/styles";
import {
  Stack,
  Drawer,
  Divider,
  Typography,
  Tabs,
  Tab,
  Box,
  Button,
} from "@mui/material";
// components
import Scrollbar from "../../components/scrollbar";
//
import { useBitrixContext } from "../../api/bitrix/context/BitrixContext";
import CompanyViewToolbar from "./CompanyViewToolbar";
import { useSharedContext } from "../SharedComponentsContext";
import OrdersTable, { OrdersTableVariants } from "../../pages/Desk/OrdersTable";
import { format } from "date-fns";
import InvoicesTable, {
  InvoicesTableVariants,
} from "../../pages/CRM/Invoices/InvoicesTable";
import QuotesTable, {
  QuotesTableVariants,
} from "../../pages/CRM/Quotes/QuoteTable";
import axiosInstance from "../../utils/axios";
import { dbContact } from "../../api/parnerus/types/contact";
import { useAuthContext } from "../../auth/useAuthContext";
import Iconify from "../../components/iconify";
import OrderAttachments from "../OrderViewSlider/OrderAttachments";
import { CustomFile, Upload } from "../../components/upload";
import AWS from "aws-sdk";
import { uploadFileAndGetLocation } from "../OrderViewSlider/OrderMessages";
import { updateCompany } from "../../api/bitrix/company";
import useResponsive from "../../hooks/useResponsive";

// ----------------------------------------------------------------------

const StyledLabel = styled("span")(({ theme }) => ({
  ...theme.typography.caption,
  width: 120,
  flexShrink: 0,
  color: theme.palette.text.secondary,
}));

// ----------------------------------------------------------------------

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`contact-tabpanel-${index}`}
      aria-labelledby={`contact-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

type Props = {
  isOpen: boolean;
};

export default function OrderDetails({ isOpen }: Props) {
  const {
    closeCompanyViewSlider,
    openContactViewSlider,
    company,
    setCompany,
    triggerLoading,
  } = useSharedContext();
  const { account } = useAuthContext();
  const isMobile = useResponsive("down", "sm");
  const [contactList, setContactList] = useState<dbContact[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);

  // Files
  const handleDropMultiFile = useCallback(
    (acceptedFiles: File[]) => {
      setFiles([
        ...files,
        ...acceptedFiles.map((newFile) =>
          Object.assign(newFile, {
            preview: URL.createObjectURL(newFile),
          })
        ),
      ]);
    },
    [files]
  );

  const handleRemoveFile = (inputFile: File | string) => {
    const filesFiltered = files.filter(
      (fileFiltered) => fileFiltered !== inputFile
    );
    setFiles(filesFiltered);
  };

  const handleRemoveAllFiles = () => {
    setFiles([]);
  };

  const handleUploadFiles = async () => {
    setLoading(true);
    const filesUrls: string[] = [];
    if (files) {
      AWS.config.update({
        accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID_S3,
        secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY_S3,
        region: "us-east-1",
      });

      const s3 = new AWS.S3();

      for (const newFile of files) {
        filesUrls.push(await uploadFileAndGetLocation(newFile, s3));
      }
    }

    const companyUpdated = (
      await axiosInstance.post("/api/company/upload-files", {
        companyId: company?.companyId,
        files: filesUrls,
        currentFiles: company?.files || [],
      })
    ).data.results.Attributes;

    if (company?.ID) {
      await updateCompany({
        ID: company?.ID,
        UF_CRM_1787075324: companyUpdated.files,
      });
    }

    setCompany(companyUpdated);
    setFiles([]);
    setLoading(false);
    triggerLoading();
  };

  const handleOnRemoveFile = async (file: CustomFile | string) => {
    const companyUpdated = (
      await axiosInstance.post("/api/company/remove-file", {
        companyId: company?.companyId,
        fileToRemove: file as string,
        currentFiles: company?.files || [],
      })
    ).data.results.Attributes;

    if (company?.ID) {
      await updateCompany({
        ID: company?.ID,
        UF_CRM_1787075324: companyUpdated.files,
      });
    }

    setCompany(companyUpdated);
    triggerLoading();
  };

  //End files function

  const [tabSelected, setTabSelected] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabSelected(newValue);
  };

  const { userfieldList, listFields } = useBitrixContext();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [liked, setLiked] = useState(false);

  const [completed, setCompleted] = useState(true);

  const handleLiked = () => {
    setLiked(!liked);
  };

  const handleCompleted = () => {
    setCompleted(!completed);
  };

  const handleClickAttach = () => {
    fileInputRef.current?.click();
  };

  useEffect(() => {
    setTabSelected(0);
  }, [isOpen]);

  useEffect(() => {
    if (account && isOpen) {
      axiosInstance
        .get(`/api/contact/list-by-name?NAME=&accountId=${account.accountId}`)
        .then((response) => setContactList(response.data.results));
    }
  }, [account, isOpen]);

  return (
    <>
      <Drawer
        open={isOpen}
        onClose={closeCompanyViewSlider}
        anchor="right"
        PaperProps={{
          sx: {
            width: {
              xs: 1,
              md: 900,
            },
          },
        }}
      >
        <CompanyViewToolbar
          fileInputRef={fileInputRef}
          liked={liked}
          completed={completed}
          onLike={handleLiked}
          onAttach={handleClickAttach}
          onCompleted={handleCompleted}
          onCloseDetails={closeCompanyViewSlider}
        />

        <Divider />

        <Scrollbar>
          <Stack spacing={3} sx={{ px: 2.5, pt: 1, pb: 5 }}>
            <Tabs
              value={tabSelected}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Información general" />
              <Tab label="Cotizaciones" />
              <Tab label="Facturas" />
              <Tab label="Órdenes" />
            </Tabs>

            <CustomTabPanel value={tabSelected} index={0}>
              <Stack spacing={3}>
                <Typography variant="overline">Información</Typography>

                <Stack direction={isMobile ? "column" : "row"} spacing={3}>
                  <Stack direction="column" width={230}>
                    <StyledLabel
                      sx={{
                        height: 10,
                        lineHeight: "10px",
                        my: 0.5,
                        width: 230,
                      }}
                    >
                      Nombre de la Empresa
                    </StyledLabel>
                    <Stack direction="row" flexWrap="wrap" alignItems="center">
                      {company?.TITLE || "-"}
                    </Stack>
                  </Stack>

                  <Stack direction="column" width={230}>
                    <StyledLabel
                      sx={{ height: 10, lineHeight: "10px", my: 0.5 }}
                    >
                      Sector de actividad
                    </StyledLabel>
                    <Stack direction="row" flexWrap="wrap" alignItems="center">
                      {company?.INDUSTRY
                        ? listFields
                            .find((lf) => lf.FIELD_NAME === "INDUSTRY")
                            ?.LIST.find((v) => v.STATUS_ID === company.INDUSTRY)
                            ?.NAME
                        : "-"}
                    </Stack>
                  </Stack>
                </Stack>

                <Stack direction={isMobile ? "column" : "row"} spacing={3}>
                  <Stack direction="column" width={230}>
                    <StyledLabel
                      sx={{
                        height: 10,
                        lineHeight: "10px",
                        my: 0.5,
                        width: 230,
                      }}
                    >
                      State of Incorporation
                    </StyledLabel>
                    <Stack direction="row" flexWrap="wrap" alignItems="center">
                      {company?.UF_STATE_OF_INCORPORATION
                        ? userfieldList
                            .find((u) => u.FIELD_NAME === "UF_CRM_1787074949")
                            ?.LIST.find(
                              (v) => v.ID === company.UF_STATE_OF_INCORPORATION
                            )?.VALUE
                        : "-"}
                    </Stack>
                  </Stack>

                  <Stack direction="column" width={230}>
                    <StyledLabel
                      sx={{ height: 10, lineHeight: "10px", my: 0.5 }}
                    >
                      IRS Status
                    </StyledLabel>
                    <Stack direction="row" flexWrap="wrap" alignItems="center">
                      {company?.UF_IRS_STATUS
                        ? userfieldList
                            .find((u) => u.FIELD_NAME === "UF_CRM_1787074855")
                            ?.LIST.find((v) => v.ID === company.UF_IRS_STATUS)
                            ?.VALUE
                        : "-"}
                    </Stack>
                  </Stack>
                </Stack>

                <StyledLabel sx={{ height: 10, lineHeight: "10px", pt: 2 }}>
                  Contacto Principal
                </StyledLabel>
                <Stack direction="row" spacing={3}>
                  {company?.primaryContactId ? (
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      alignItems="center"
                      onClick={() =>
                        openContactViewSlider(
                          contactList.find(
                            (c) => c.contactId === company.primaryContactId
                          )
                        )
                      }
                      sx={{ cursor: "pointer", textTransform: "capitalize" }}
                    >
                      {`${
                        contactList.find(
                          (c) => c.contactId === company.primaryContactId
                        )?.NAME
                      } ${
                        contactList.find(
                          (c) => c.contactId === company.primaryContactId
                        )?.LAST_NAME
                      }`}
                      <Iconify icon="ph:link-light" />
                    </Stack>
                  ) : (
                    <Stack direction="row" flexWrap="wrap" alignItems="center">
                      -
                    </Stack>
                  )}
                </Stack>

                <Divider />

                <Typography variant="overline" sx={{ pt: 3 }}>
                  DETALLES
                </Typography>

                <Stack direction={isMobile ? "column" : "row"} spacing={3}>
                  <Stack direction="column" width={180}>
                    <StyledLabel
                      sx={{ height: 10, lineHeight: "10px", my: 0.5 }}
                    >
                      Type of service
                    </StyledLabel>
                    <Stack direction="row" flexWrap="wrap" alignItems="center">
                      {company?.UF_TYPE_OF_SERVICE &&
                      company?.UF_TYPE_OF_SERVICE.length
                        ? company.UF_TYPE_OF_SERVICE.map(
                            (value) =>
                              userfieldList
                                .find(
                                  (u) => u.FIELD_NAME === "UF_CRM_1787075056"
                                )
                                ?.LIST.find((v) => v.ID === value)?.VALUE
                          ).join(", ")
                        : "-"}
                    </Stack>
                  </Stack>

                  <Stack direction="column" width={180}>
                    <StyledLabel
                      sx={{ height: 10, lineHeight: "10px", my: 0.5 }}
                    >
                      Incorporation date
                    </StyledLabel>
                    <Stack direction="row" flexWrap="wrap" alignItems="center">
                      {company?.UF_INCORPORATION_DATE
                        ? format(
                            new Date(company.UF_INCORPORATION_DATE),
                            "dd MMM yyyy"
                          )
                        : "-"}
                    </Stack>
                  </Stack>

                  <Stack direction="column" width={180}>
                    <StyledLabel
                      sx={{ height: 10, lineHeight: "10px", my: 0.5 }}
                    >
                      Sitio Web
                    </StyledLabel>
                    <Stack direction="row" flexWrap="wrap" alignItems="center">
                      {company?.WEB || "-"}
                    </Stack>
                  </Stack>

                  <Stack direction="column" width={300}>
                    <StyledLabel
                      sx={{ height: 10, lineHeight: "10px", my: 0.5 }}
                    >
                      Comentarios
                    </StyledLabel>
                    <Stack direction="row" flexWrap="wrap" alignItems="center">
                      {company?.COMMENTS || "-"}
                    </Stack>
                  </Stack>
                </Stack>

                <Divider />

                <Typography variant="overline">DOCUMENTOS</Typography>
                <Stack direction="column" gap={2}>
                  {company?.files && company.files.length ? (
                    <OrderAttachments
                      attachments={company.files}
                      onRemove={handleOnRemoveFile}
                    />
                  ) : (
                    ""
                  )}
                  <Stack width={isMobile ? "100%" : 500}>
                    {files && files.length ? (
                      <Button
                        variant="contained"
                        onClick={handleUploadFiles}
                        disabled={loading}
                        sx={{ width: 100, mb: 2 }}
                      >
                        Guardar
                      </Button>
                    ) : (
                      ""
                    )}
                    <Upload
                      multiple
                      files={files}
                      onDrop={handleDropMultiFile}
                      onRemove={handleRemoveFile}
                      onRemoveAll={handleRemoveAllFiles}
                      onUpload={() => console.log("ON UPLOAD")}
                      disabled={loading}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </CustomTabPanel>

            <CustomTabPanel value={tabSelected} index={1}>
              <Stack spacing={3}>
                {company && (
                  <QuotesTable
                    variant={QuotesTableVariants.ContactAndCompany}
                    filterAdded={{
                      field: "companyId",
                      value: company.companyId as string,
                    }}
                  />
                )}
              </Stack>
            </CustomTabPanel>

            <CustomTabPanel value={tabSelected} index={2}>
              <Stack spacing={3}>
                {company && (
                  <InvoicesTable
                    variant={InvoicesTableVariants.ContactAndCompany}
                    filterAdded={{
                      field: "companyId",
                      value: company.companyId as string,
                    }}
                  />
                )}
              </Stack>
            </CustomTabPanel>

            <CustomTabPanel value={tabSelected} index={3}>
              <Stack spacing={3}>
                <OrdersTable
                  variant={OrdersTableVariants.Contact}
                  filtersAdded={[
                    { field: "COMPANY_ID", value: company?.ID || "99999999" },
                  ]}
                />
              </Stack>
            </CustomTabPanel>
          </Stack>
        </Scrollbar>
      </Drawer>
    </>
  );
}
